import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getIn, useFormikContext } from 'formik';
import { Autocomplete, Box, IconButton, Typography } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { Text } from '@app/ui/forms';
import { Tooltip } from '@app/ui/tooltip';

import { BOOLEAN_VALUES, MATCHING_ENTITY_VALUES } from '../../constants';
import { ConditionRowErrorTarget, MatchingEntity, MatchingOperator, RuleField } from '../../types';
import {
  getMatchingValueControl,
  legalMatchingOperatorsFor,
  matchingConditionRowIssue,
  matchingOperatorNeedsRight,
  matchingOperatorWireName,
  MatchingConditionFormValue,
} from '../../utils';
import { MatchingFieldAutocomplete } from './MatchingFieldAutocomplete';

export interface MatchingConditionRowProps {
  readonly name: string;
  readonly index: number;
  readonly fields: RuleField[];
  // Keyed `"<entity>:<path>"` — a matching condition can compare a GC field against an IP field, so
  // a plain path→field map (colour's shape) cannot disambiguate.
  readonly fieldsByEntityAndPath: Map<string, RuleField>;
  readonly disabled: boolean;
  readonly canRemove: boolean;
  readonly canMoveUp: boolean;
  readonly canMoveDown: boolean;
  readonly onRemove: () => void;
  readonly onMove: (direction: -1 | 1) => void;
}

// One condition of an `exact` matching rule's `grades[]` group (SPEC §6.3): a LEFT field (entity +
// path), an operator filtered by the left field's type, and — depending on the operator — a RIGHT
// field on the other entity, a literal value, or neither (`is_blank`/`is_not_blank`). Mirrors
// `ConditionRow`/`ColorCaseRow`, widened for the entity dimension.
export const MatchingConditionRow = ({
  name,
  index,
  fields,
  fieldsByEntityAndPath,
  disabled,
  canRemove,
  canMoveUp,
  canMoveDown,
  onRemove,
  onMove,
}: MatchingConditionRowProps) => {
  const { t } = useTranslation('common');
  const { values, setFieldValue, setFieldTouched, submitCount, touched } = useFormikContext<unknown>();

  const row = getIn(values, name) as MatchingConditionFormValue | undefined;

  const leftFields = useMemo(
    () => (row?.leftEntity ? fields.filter(field => field.entity === row.leftEntity) : []),
    [fields, row?.leftEntity],
  );
  const rightFields = useMemo(
    () => (row?.rightEntity ? fields.filter(field => field.entity === row.rightEntity) : []),
    [fields, row?.rightEntity],
  );

  const leftField = row?.leftEntity ? fieldsByEntityAndPath.get(`${row.leftEntity}:${row.leftField}`) : undefined;
  const rightField = row?.rightEntity ? fieldsByEntityAndPath.get(`${row.rightEntity}:${row.rightField}`) : undefined;

  const operators = useMemo(() => legalMatchingOperatorsFor(leftField), [leftField?.path, leftField?.operators]);
  const control = getMatchingValueControl(leftField?.type, row?.operator);
  const needsRight = matchingOperatorNeedsRight(row?.operator);

  const showError = submitCount > 0 || !!getIn(touched, name);
  const issue =
    showError && row ? matchingConditionRowIssue(row, fieldsByEntityAndPath, key => t(key) ?? key) : undefined;
  const errorFor = (target: ConditionRowErrorTarget) => (issue?.target === target ? issue.message : undefined);
  const valueError = errorFor(ConditionRowErrorTarget.value);
  const markTouched = (suffix: string) => setFieldTouched(`${name}.${suffix}`, true);

  const operatorOptions = useMemo(
    () =>
      operators.map(operator => ({
        id: operator,
        name: t(`rules.matching.operator.option.${matchingOperatorWireName(operator)}`) ?? operator,
      })),
    [operators, t],
  );

  const optionChoices = useMemo(
    () => (leftField?.options ?? []).map(option => ({ id: option.key, name: option.label })),
    [leftField],
  );

  if (!row) {
    return null;
  }

  const handleLeftEntityChange = (entity: MatchingEntity | '') =>
    setFieldValue(name, { ...row, leftEntity: entity, leftField: '', operator: '', value: '', values: [] });

  const handleLeftFieldChange = (next: RuleField | null) =>
    setFieldValue(name, { ...row, leftField: next?.path ?? '', operator: '', value: '', values: [] });

  const handleOperatorChange = (next: string) =>
    setFieldValue(name, {
      ...row,
      operator: next as MatchingOperator,
      value: '',
      values: [],
      ...(matchingOperatorNeedsRight(next as MatchingOperator) ? {} : { rightEntity: '', rightField: '' }),
    });

  const handleRightEntityChange = (entity: MatchingEntity | '') =>
    setFieldValue(name, { ...row, rightEntity: entity, rightField: '' });

  return (
    <Box sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: '16px', p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h4">{t('rules.editor.condition.heading', { number: index + 1 })}</Typography>

        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title={t('rules.editor.condition.moveUpButton') ?? undefined}>
            <span>
              <IconButton size="small" disabled={disabled || !canMoveUp} onClick={() => onMove(-1)}>
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('rules.editor.condition.moveDownButton') ?? undefined}>
            <span>
              <IconButton size="small" disabled={disabled || !canMoveDown} onClick={() => onMove(1)}>
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('rules.editor.condition.removeButton') ?? undefined}>
            <span>
              <IconButton size="small" disabled={disabled || !canRemove} onClick={onRemove}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {t('rules.matching.condition.leftLabel')}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box sx={{ width: { xs: '100%', sm: 140 } }}>
          <Text
            select
            disabled={disabled}
            label={t('rules.matching.source.entityLabel')}
            value={row.leftEntity}
            onChange={event => handleLeftEntityChange(event.target.value as MatchingEntity)}
            options={MATCHING_ENTITY_VALUES.map(entity => ({
              id: entity,
              name: t(`rules.matching.entity.option.${entity}`) ?? entity,
            }))}
          />
        </Box>

        <Box sx={{ flexGrow: 1, minWidth: 220 }}>
          <MatchingFieldAutocomplete
            disabled={disabled || !row.leftEntity}
            fields={leftFields}
            value={leftField ?? null}
            onChange={handleLeftFieldChange}
            onBlur={() => markTouched('leftField')}
            label={t('rules.editor.condition.fieldLabel')}
            helperText={t('rules.editor.condition.fieldHelperText')}
            error={errorFor(ConditionRowErrorTarget.field)}
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={theme => ({ width: { xs: '100%', sm: theme.spacing(36) } })}>
          <Text
            select
            disabled={disabled || !leftField}
            label={t('rules.editor.condition.operatorLabel')}
            error={!!errorFor(ConditionRowErrorTarget.operator)}
            helperText={errorFor(ConditionRowErrorTarget.operator)}
            value={row.operator}
            onChange={event => handleOperatorChange(event.target.value)}
            onBlur={() => markTouched('operator')}
            options={operatorOptions}
          />
        </Box>

        {needsRight && (
          <>
            <Box sx={{ width: { xs: '100%', sm: 140 } }}>
              <Text
                select
                disabled={disabled}
                label={t('rules.matching.condition.rightEntityLabel')}
                value={row.rightEntity}
                onChange={event => handleRightEntityChange(event.target.value as MatchingEntity)}
                options={MATCHING_ENTITY_VALUES.map(entity => ({
                  id: entity,
                  name: t(`rules.matching.entity.option.${entity}`) ?? entity,
                }))}
              />
            </Box>

            <Box sx={{ flexGrow: 1, minWidth: 200 }}>
              <MatchingFieldAutocomplete
                disabled={disabled || !row.rightEntity}
                fields={rightFields}
                value={rightField ?? null}
                onChange={next => setFieldValue(name, { ...row, rightField: next?.path ?? '' })}
                onBlur={() => markTouched('rightField')}
                label={t('rules.matching.condition.rightFieldLabel')}
                error={errorFor(ConditionRowErrorTarget.rightField)}
              />
            </Box>
          </>
        )}

        <Box sx={{ flexGrow: 1, minWidth: 200 }} onBlur={() => markTouched('value')}>
          {control === 'none' && !!row.operator && !needsRight && (
            <Typography variant="body2" color="text.secondary">
              {t('rules.editor.condition.noValueNeeded')}
            </Typography>
          )}

          {control === 'boolean' && (
            <Text
              select
              disabled={disabled}
              label={t('rules.editor.condition.valueLabel')}
              error={!!valueError}
              helperText={valueError}
              value={row.value}
              onChange={event => setFieldValue(`${name}.value`, event.target.value)}
              options={BOOLEAN_VALUES.map(value => ({
                id: value,
                name: t(`general.option.${value === 'true' ? 'yes' : 'no'}`) ?? value,
              }))}
            />
          )}

          {control === 'optionSelect' && (
            <Text
              select
              disabled={disabled}
              label={t('rules.editor.condition.valueLabel')}
              error={!!valueError}
              helperText={valueError}
              value={row.value}
              onChange={event => setFieldValue(`${name}.value`, event.target.value)}
              options={optionChoices}
            />
          )}

          {control === 'optionMultiSelect' && (
            <Text
              select
              disabled={disabled}
              SelectProps={{ multiple: true }}
              label={t('rules.editor.condition.valuesLabel')}
              error={!!valueError}
              helperText={valueError}
              value={row.values}
              onChange={event => setFieldValue(`${name}.values`, event.target.value)}
              options={optionChoices}
            />
          )}

          {control === 'textList' && (
            <Autocomplete
              multiple
              freeSolo
              disabled={disabled}
              options={[] as string[]}
              value={row.values ?? []}
              onChange={(_event, next) => setFieldValue(`${name}.values`, next)}
              renderInput={params => (
                <Text
                  {...params}
                  label={t('rules.editor.condition.valuesLabel')}
                  error={!!valueError}
                  helperText={valueError}
                />
              )}
            />
          )}

          {control === 'number' && (
            <Text
              disabled={disabled}
              label={
                matchingOperatorNeedsRight(row.operator)
                  ? t('rules.matching.condition.windowValueLabel')
                  : t('rules.editor.condition.valueLabel')
              }
              error={!!valueError}
              helperText={valueError}
              value={row.value}
              onChange={event => setFieldValue(`${name}.value`, event.target.value)}
            />
          )}

          {control === 'text' && (
            <Text
              disabled={disabled}
              label={t('rules.editor.condition.valueLabel')}
              error={!!valueError}
              helperText={valueError}
              value={row.value}
              onChange={event => setFieldValue(`${name}.value`, event.target.value)}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};
